import { useMemo, useState } from 'react';
import type { AppConfig } from '@shared/types';
import { compareSemver, isValidSemver } from '@shared/utils/semver';
import ConfirmModal from '../../components/ConfirmModal';
import TypedConfirmModal from '../../components/TypedConfirmModal';
import type { AppConfigPatch } from '../../services/config';

interface ConfigFormProps {
  config: AppConfig;
  readOnly: boolean;
  busy: boolean;
  onSave: (patch: AppConfigPatch) => Promise<void>;
}

interface FormState {
  iosMin: string;
  androidMin: string;
  adProvider: AppConfig['ads']['provider'];
  bannerEnabled: boolean;
  interstitialEnabled: boolean;
  rewardedEnabled: boolean;
  maintenanceEnabled: boolean;
  maintenanceMessageKey: string;
  killSwitch: boolean;
  crashlyticsEnabled: boolean;
  funnelTelemetryEnabled: boolean;
  answerFeedSelfSide: AppConfig['gameplay']['answerFeedSelfSide'];
  homeLayout: AppConfig['ui']['homeLayout'];
  maxEnergy: number;
  regenIntervalMinutes: number;
  startingEnergy: number;
  matchCostPerMatch: number;
  dailyChallengeRequiredWins: number;
}

const ECONOMY_FIELDS: ReadonlyArray<{
  key:
    | 'maxEnergy'
    | 'regenIntervalMinutes'
    | 'startingEnergy'
    | 'matchCostPerMatch'
    | 'dailyChallengeRequiredWins';
  label: string;
  min: number;
  max: number;
}> = [
  { key: 'maxEnergy', label: 'Max energy (regen cap)', min: 1, max: 999 },
  { key: 'regenIntervalMinutes', label: 'Regen interval (minutes)', min: 1, max: 1440 },
  { key: 'startingEnergy', label: 'Starting energy (new users)', min: 0, max: 999 },
  { key: 'matchCostPerMatch', label: 'Match cost (0 = free)', min: 0, max: 99 },
  // Min 1, never 0 — a 0 target claims on the first win of the day, which
  // is just first-win-of-day again. Mirrors the Zod bound in
  // `validators/adminConfig.ts`.
  {
    key: 'dailyChallengeRequiredWins',
    label: 'Daily challenge (wins to win)',
    min: 1,
    max: 99,
  },
];

function toFormState(config: AppConfig): FormState {
  return {
    iosMin: config.minimumVersion.ios,
    androidMin: config.minimumVersion.android,
    adProvider: config.ads.provider,
    bannerEnabled: config.ads.banner.enabled,
    interstitialEnabled: config.ads.interstitial.enabled,
    rewardedEnabled: config.ads.rewarded.enabled,
    maintenanceEnabled: config.maintenance.enabled,
    maintenanceMessageKey: config.maintenance.messageKey ?? '',
    killSwitch: config.killSwitch.global,
    crashlyticsEnabled: config.observability.crashlyticsEnabled,
    funnelTelemetryEnabled: config.observability.funnelTelemetryEnabled ?? true,
    answerFeedSelfSide: config.gameplay.answerFeedSelfSide,
    homeLayout: config.ui?.homeLayout ?? 'classic',
    maxEnergy: config.economy.maxEnergy,
    regenIntervalMinutes: config.economy.regenIntervalMinutes,
    startingEnergy: config.economy.startingEnergy,
    matchCostPerMatch: config.economy.matchCostPerMatch,
    dailyChallengeRequiredWins: config.economy.dailyChallengeRequiredWins,
  };
}

function diffPatch(before: FormState, after: FormState): AppConfigPatch {
  const patch: AppConfigPatch = {};
  if (before.iosMin !== after.iosMin || before.androidMin !== after.androidMin) {
    patch.minimumVersion = {
      ...(before.iosMin !== after.iosMin && { ios: after.iosMin }),
      ...(before.androidMin !== after.androidMin && {
        android: after.androidMin,
      }),
    };
  }
  const ads: Partial<AppConfig['ads']> = {};
  if (before.adProvider !== after.adProvider) {
    ads.provider = after.adProvider;
  }
  if (before.bannerEnabled !== after.bannerEnabled) {
    ads.banner = { enabled: after.bannerEnabled };
  }
  if (before.interstitialEnabled !== after.interstitialEnabled) {
    ads.interstitial = { enabled: after.interstitialEnabled };
  }
  if (before.rewardedEnabled !== after.rewardedEnabled) {
    ads.rewarded = { enabled: after.rewardedEnabled };
  }
  if (Object.keys(ads).length > 0) {
    patch.ads = ads;
  }
  if (
    before.maintenanceEnabled !== after.maintenanceEnabled ||
    before.maintenanceMessageKey !== after.maintenanceMessageKey
  ) {
    patch.maintenance = {
      ...(before.maintenanceEnabled !== after.maintenanceEnabled && {
        enabled: after.maintenanceEnabled,
      }),
      ...(before.maintenanceMessageKey !== after.maintenanceMessageKey && {
        messageKey: after.maintenanceMessageKey || undefined,
      }),
    };
  }
  if (before.killSwitch !== after.killSwitch) {
    patch.killSwitch = { global: after.killSwitch };
  }
  if (before.crashlyticsEnabled !== after.crashlyticsEnabled) {
    patch.observability = { crashlyticsEnabled: after.crashlyticsEnabled };
  }
  if (before.funnelTelemetryEnabled !== after.funnelTelemetryEnabled) {
    patch.observability = {
      ...(patch.observability ?? {}),
      funnelTelemetryEnabled: after.funnelTelemetryEnabled,
    };
  }
  if (before.answerFeedSelfSide !== after.answerFeedSelfSide) {
    patch.gameplay = { answerFeedSelfSide: after.answerFeedSelfSide };
  }
  if (before.homeLayout !== after.homeLayout) {
    patch.ui = { homeLayout: after.homeLayout };
  }
  const economy: Partial<AppConfig['economy']> = {};
  for (const { key } of ECONOMY_FIELDS) {
    if (before[key] !== after[key]) economy[key] = after[key];
  }
  if (Object.keys(economy).length > 0) {
    patch.economy = economy;
  }
  return patch;
}

/** A change is high-risk if it would broadly impact or block users. */
function riskLevel(
  before: FormState,
  after: FormState
): 'high' | 'low' | 'none' {
  if (
    before.maintenanceEnabled !== after.maintenanceEnabled &&
    after.maintenanceEnabled
  )
    return 'high';
  if (before.killSwitch !== after.killSwitch && after.killSwitch) return 'high';
  // Switching the ad SDK (or turning ads off entirely) changes monetization
  // for the whole fleet on their next launch — always the typed confirmation.
  if (before.adProvider !== after.adProvider) return 'high';
  if (before.bannerEnabled && !after.bannerEnabled) return 'high';
  if (before.interstitialEnabled && !after.interstitialEnabled) return 'high';
  if (before.rewardedEnabled && !after.rewardedEnabled) return 'high';
  // Disabling Crashlytics blinds the team to live crashes — treat as high
  // risk. Re-enabling is a recovery path and is low risk.
  if (before.crashlyticsEnabled && !after.crashlyticsEnabled) return 'high';
  if (
    isValidSemver(before.iosMin) &&
    isValidSemver(after.iosMin) &&
    compareSemver(after.iosMin, before.iosMin) > 0
  )
    return 'high';
  if (
    isValidSemver(before.androidMin) &&
    isValidSemver(after.androidMin) &&
    compareSemver(after.androidMin, before.androidMin) > 0
  )
    return 'high';
  // Energy economy edits change the live F2P gate for everyone — treat any
  // change as high-risk so it goes through the typed confirmation.
  const economyDirty = ECONOMY_FIELDS.some(({ key }) => before[key] !== after[key]);
  if (economyDirty) return 'high';

  const dirty =
    before.iosMin !== after.iosMin ||
    before.androidMin !== after.androidMin ||
    before.adProvider !== after.adProvider ||
    before.bannerEnabled !== after.bannerEnabled ||
    before.interstitialEnabled !== after.interstitialEnabled ||
    before.rewardedEnabled !== after.rewardedEnabled ||
    before.maintenanceEnabled !== after.maintenanceEnabled ||
    before.maintenanceMessageKey !== after.maintenanceMessageKey ||
    before.killSwitch !== after.killSwitch ||
    before.crashlyticsEnabled !== after.crashlyticsEnabled ||
    // Funnel telemetry (#764) is analytics only — low risk either way.
    before.funnelTelemetryEnabled !== after.funnelTelemetryEnabled ||
    // Feed-side flip is a cosmetic layout change — low risk, plain confirm.
    before.answerFeedSelfSide !== after.answerFeedSelfSide ||
    // Home layout (#875) is presentation only — low risk, plain confirm.
    before.homeLayout !== after.homeLayout;
  return dirty ? 'low' : 'none';
}

const REQUIRED_CONFIRM_TEXT = 'APPLY CONFIG';

export default function ConfigForm({
  config,
  readOnly,
  busy,
  onSave,
}: ConfigFormProps) {
  const initial = useMemo(() => toFormState(config), [config]);
  const [state, setState] = useState<FormState>(initial);
  const [confirmKind, setConfirmKind] = useState<null | 'low' | 'high'>(null);
  const [error, setError] = useState<string | null>(null);

  const risk = riskLevel(initial, state);
  const invalidSemver =
    !isValidSemver(state.iosMin) || !isValidSemver(state.androidMin);
  const invalidEconomy = ECONOMY_FIELDS.some(
    ({ key, min, max }) =>
      !Number.isInteger(state[key]) || state[key] < min || state[key] > max
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (readOnly || busy) return;
    if (invalidSemver) {
      setError('Minimum version must be a MAJOR.MINOR.PATCH semver string.');
      return;
    }
    if (invalidEconomy) {
      setError('Energy economy values must be whole numbers within range.');
      return;
    }
    setError(null);
    if (risk === 'none') return;
    setConfirmKind(risk === 'high' ? 'high' : 'low');
  }

  async function runSave() {
    const patch = diffPatch(initial, state);
    try {
      await onSave(patch);
      setConfirmKind(null);
    } catch (err) {
      setConfirmKind(null);
      setError(
        err instanceof Error ? err.message : 'Could not apply the patch.'
      );
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
      data-testid="config-form"
    >
      {error && (
        <div
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
          data-testid="config-form-error"
        >
          {error}
        </div>
      )}

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-700">
          Minimum supported version (semver)
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-slate-600">iOS</span>
            <input
              type="text"
              value={state.iosMin}
              disabled={readOnly || busy}
              onChange={(e) => setState({ ...state, iosMin: e.target.value })}
              data-testid="config-min-ios"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-600">Android</span>
            <input
              type="text"
              value={state.androidMin}
              disabled={readOnly || busy}
              onChange={(e) =>
                setState({ ...state, androidMin: e.target.value })
              }
              data-testid="config-min-android"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </label>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-700">Ads</h3>
        <label className="block text-sm">
          <span className="text-slate-600">Mediation provider</span>
          <select
            value={state.adProvider}
            disabled={readOnly || busy}
            onChange={(e) =>
              setState({
                ...state,
                adProvider: e.target.value as AppConfig['ads']['provider'],
              })
            }
            data-testid="config-ad-provider"
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
          >
            {/*
              AppLovin MAX is present but DISABLED — not removed. The SDK,
              adapter, and config plugin all remain in the app so it can be
              re-enabled later; this is a policy gate, not a teardown. The
              published privacy policy names Google AdMob as the sole ad
              partner, and AppLovin sets no consent flag on Android (#548),
              so selecting it would serve ads from an undisclosed network.
              The server validator rejects the write too — this keeps the UI
              honest rather than being the only guard.

              Kept as a disabled option rather than deleted so a legacy row
              still holding 'applovin' RENDERS its real value instead of the
              select silently falling back to AdMob and hiding the state.

              To re-enable: land #548, restore AppLovin to both privacy
              renderings, then drop `disabled` here AND add it back to the
              `provider` enum in `server/src/validators/adminConfig.ts`.
            */}
            <option value="applovin" disabled>
              AppLovin MAX (unavailable — see #548)
            </option>
            <option value="admob">Google AdMob</option>
            <option value="none">None (no ad SDK initialized)</option>
          </select>
          <span className="block text-xs text-slate-500 mt-1">
            Clients lock the provider in at launch — a change applies on each
            user&apos;s next app start, not mid-session. AppLovin MAX is
            unavailable while the privacy policy names AdMob only (#548).
          </span>
        </label>
        <div className="space-y-1">
          {(
            [
              ['banner', 'bannerEnabled'] as const,
              ['interstitial', 'interstitialEnabled'] as const,
              ['rewarded', 'rewardedEnabled'] as const,
            ]
          ).map(([label, field]) => (
            <label key={label} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={state[field]}
                disabled={readOnly || busy}
                onChange={(e) =>
                  setState({ ...state, [field]: e.target.checked })
                }
                data-testid={`config-ad-${label}`}
              />
              <span className="capitalize">{label}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-700">Maintenance</h3>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={state.maintenanceEnabled}
            disabled={readOnly || busy}
            onChange={(e) =>
              setState({ ...state, maintenanceEnabled: e.target.checked })
            }
            data-testid="config-maintenance-enabled"
          />
          <span>Maintenance mode enabled</span>
        </label>
        <label className="block text-sm">
          <span className="text-slate-600">Message key (i18n)</span>
          <input
            type="text"
            value={state.maintenanceMessageKey}
            placeholder="e.g. maintenance.scheduled"
            disabled={readOnly || busy}
            onChange={(e) =>
              setState({ ...state, maintenanceMessageKey: e.target.value })
            }
            data-testid="config-maintenance-key"
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
          />
        </label>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-700">Observability</h3>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={state.crashlyticsEnabled}
            disabled={readOnly || busy}
            onChange={(e) =>
              setState({ ...state, crashlyticsEnabled: e.target.checked })
            }
            data-testid="config-crashlytics-enabled"
          />
          <span>Firebase Crashlytics enabled (crash reports)</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={state.funnelTelemetryEnabled}
            disabled={readOnly || busy}
            onChange={(e) =>
              setState({ ...state, funnelTelemetryEnabled: e.target.checked })
            }
            data-testid="config-funnel-telemetry-enabled"
          />
          <span>First-session funnel telemetry enabled</span>
        </label>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-700">Gameplay</h3>
        <label className="block text-sm">
          <span className="text-slate-600">
            Answer feed — player&apos;s own answers align
          </span>
          <select
            value={state.answerFeedSelfSide}
            disabled={readOnly || busy}
            onChange={(e) =>
              setState({
                ...state,
                answerFeedSelfSide: e.target
                  .value as AppConfig['gameplay']['answerFeedSelfSide'],
              })
            }
            data-testid="config-answer-feed-self-side"
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
          >
            <option value="right">Right (messaging convention, default)</option>
            <option value="left">Left (legacy layout)</option>
          </select>
          <span className="block text-xs text-slate-500 mt-1">
            Presentation only — the opponent always renders on the opposite
            side. Applies to live clients on their next config refresh.
          </span>
        </label>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-700">Home screen</h3>
        <label className="block text-sm">
          <span className="text-slate-600">Home layout</span>
          <select
            value={state.homeLayout}
            disabled={readOnly || busy}
            onChange={(e) =>
              setState({
                ...state,
                homeLayout: e.target.value as AppConfig['ui']['homeLayout'],
              })
            }
            data-testid="config-home-layout"
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
          >
            <option value="classic">Classic (default)</option>
            <option value="hero">Hero (game art + PLAY bar)</option>
          </select>
          <span className="block text-xs text-slate-500 mt-1">
            Applies to this game only. Hero renders where the app version
            ships the game&apos;s hero art, in portrait on a light theme;
            everywhere else the classic Home stays. Live clients pick it up on
            their next config refresh.
          </span>
        </label>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-700">Economy</h3>
        <p className="text-xs text-slate-500">
          Live free-to-play gate and daily-challenge target. Applies to all
          users (existing + new) on the next config refresh — no app build
          required. The challenge target interacts with the energy cap: at a
          ~50% win rate, N wins costs roughly 2N matches.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {ECONOMY_FIELDS.map(({ key, label, min, max }) => (
            <label key={key} className="block text-sm">
              <span className="text-slate-600">{label}</span>
              <input
                type="number"
                value={Number.isNaN(state[key]) ? '' : state[key]}
                min={min}
                max={max}
                step={1}
                disabled={readOnly || busy}
                onChange={(e) =>
                  setState({ ...state, [key]: e.target.valueAsNumber })
                }
                data-testid={`config-economy-${key}`}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
            </label>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-700">
          Emergency stop
        </h3>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={state.killSwitch}
            disabled={readOnly || busy}
            onChange={(e) =>
              setState({ ...state, killSwitch: e.target.checked })
            }
            data-testid="config-kill-switch"
          />
          <span>Global kill switch (shows Service Unavailable gate)</span>
        </label>
      </section>

      {!readOnly && (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setState(initial)}
            disabled={busy || risk === 'none'}
            data-testid="config-form-reset"
            className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
          >
            Revert
          </button>
          <button
            type="submit"
            disabled={busy || risk === 'none' || invalidSemver || invalidEconomy}
            data-testid="config-form-save"
            className="text-sm rounded px-3 py-1.5 bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {busy ? 'Saving…' : risk === 'high' ? 'Review & apply…' : 'Save'}
          </button>
        </div>
      )}

      <ConfirmModal
        open={confirmKind === 'low'}
        title="Apply config change?"
        description="This is a low-risk change. It will take effect for all live clients within a few seconds."
        confirmLabel="Apply"
        busy={busy}
        onConfirm={runSave}
        onCancel={() => setConfirmKind(null)}
      />
      <TypedConfirmModal
        open={confirmKind === 'high'}
        title="Apply HIGH-RISK config change?"
        description="This change will impact gameplay, ads, or user access immediately. Type the phrase to confirm."
        requiredText={REQUIRED_CONFIRM_TEXT}
        confirmLabel="Apply"
        busy={busy}
        onConfirm={runSave}
        onCancel={() => setConfirmKind(null)}
      />
    </form>
  );
}
